import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "./operations.css";
import { OrderDetailModal } from "../reports/reports";
// Тот же чек, что печатается после оплаты в «Заездах» (вместе с ним подтягивается arrivals.css)
import { ReceiptPrintout, ReceiptLangModal } from "../arrivals/arrivals";

const MONTHS_RU = [
    "Январь", "Февраль", "Март", "Апрель", "Май", "Июнь",
    "Июль", "Август", "Сентябрь", "Октябрь", "Ноябрь", "Декабрь",
];

const todayISO = () => new Date().toISOString().slice(0, 10);

const NAVIGABLE_TYPES = ["individual", "sharetour", "partners"];
const ROLE_LABELS = { guide: "Гид", driver: "Водитель", cafe: "Кафе" };

// Подпись получателя: JSON {"w","h","paths":[...]} — те же линии, что нарисованы пальцем на планшете
const SignatureView = ({ signature }) => {
    let sig = null;
    try { sig = signature ? JSON.parse(signature) : null; } catch { sig = null; }
    if (!sig || !Array.isArray(sig.paths) || !sig.paths.length) {
        return <div className="op-sign-empty">Подпись не ставилась</div>;
    }
    return (
        <div className="op-sign-box">
            <svg viewBox={`0 0 ${sig.w} ${sig.h}`} preserveAspectRatio="xMidYMid meet">
                {sig.paths.map((d, i) => (
                    <path key={i} d={d} stroke="#111" strokeWidth="2.5" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                ))}
            </svg>
        </div>
    );
};

const Operations = ({ API_URL }) => {
    const now = new Date();
    const [filterMode, setFilterMode] = useState("month");
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [dateFrom, setDateFrom] = useState(todayISO());
    const [dateTo, setDateTo] = useState(todayISO());
    const [kind, setKind] = useState("all");
    const [search, setSearch] = useState("");
    const [operations, setOperations] = useState([]);
    const [totals, setTotals] = useState({});
    const [loading, setLoading] = useState(false);

    const [detailOrder, setDetailOrder] = useState(null);
    const [expenseDetail, setExpenseDetail] = useState(null); // оплата расхода + подпись
    const [detailLoadingId, setDetailLoadingId] = useState(null);

    const [annulTarget, setAnnulTarget] = useState(null);
    const [annulComment, setAnnulComment] = useState("");
    const [annulling, setAnnulling] = useState(false);

    // Повторная печать чека
    const [reprintLoadingId, setReprintLoadingId] = useState(null); // id операции, для которой грузим данные чека
    const [pendingReceipt, setPendingReceipt] = useState(null);     // данные получены, ждём выбора языка
    const [receipt, setReceipt] = useState(null);                   // язык выбран — этот объект и печатается
    const printedIdRef = React.useRef(null);

    const handleReprint = async (op, rawId) => {
        if (reprintLoadingId) return;
        setReprintLoadingId(op.id);
        try {
            const { data } = await axios.post(`${API_URL}/getReceiptForOperation`, { id: rawId });
            if (data.status === "success") setPendingReceipt(data.receipt);
            else alert(data.message || "Не удалось получить данные чека");
        } catch (err) {
            console.error("getReceiptForOperation:", err);
            alert(err.response?.data?.message || "Ошибка соединения с сервером");
        } finally {
            setReprintLoadingId(null);
        }
    };

    const handleLangPick = (lang) => {
        if (lang) setReceipt({ ...pendingReceipt, lang });
        setPendingReceipt(null);
    };

    // Та же схема, что в «Заездах»: даём React отрисовать чек в DOM, затем печатаем.
    // printedIdRef защищает от двойной печати в StrictMode.
    useEffect(() => {
        if (!receipt) return;
        if (printedIdRef.current === receipt.printId) return;
        printedIdRef.current = receipt.printId;
        const t = setTimeout(() => {
            window.print();
            setReceipt(null);
        }, 400);
        return () => clearTimeout(t);
    }, [receipt]);

    const confirmAnnul = async () => {
        if (!annulTarget) return;
        setAnnulling(true);
        try {
            const { data } = await axios.post(`${API_URL}/annulOperation`, {
                type: annulTarget.type,
                id: annulTarget.rawId,
                comment: annulComment || null,
            });
            if (data.status === "success") {
                setAnnulTarget(null);
                setAnnulComment("");
                fetchOperations();
            } else {
                alert(data.message || "Не удалось аннулировать");
            }
        } catch (err) {
            console.error("annulOperation:", err);
            alert("Ошибка соединения с сервером");
        } finally {
            setAnnulling(false);
        }
    };

    const fetchOperations = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.post(`${API_URL}/getOperations`, {
                filterMode, year, month, dateFrom, dateTo, kind,
            });
            if (data.status === "success") {
                setOperations(data.operations || []);
                setTotals(data.totals || {});
            }
        } catch (err) {
            console.error("getOperations:", err);
        } finally {
            setLoading(false);
        }
    }, [API_URL, filterMode, year, month, dateFrom, dateTo, kind]);

    useEffect(() => {
        fetchOperations();
    }, [fetchOperations]);

    const goPrevMonth = () => {
        if (month === 1) { setMonth(12); setYear((y) => y - 1); }
        else setMonth((m) => m - 1);
    };
    const goNextMonth = () => {
        if (month === 12) { setMonth(1); setYear((y) => y + 1); }
        else setMonth((m) => m + 1);
    };

    const handleRowClick = async (op) => {
        // Оплата расхода — детали и подпись получателя
        if (op.type === "expense_payment") {
            const rawId = op.id.split("-")[1];
            setDetailLoadingId(op.id);
            try {
                const { data } = await axios.post(`${API_URL}/getExpensePaymentDetail`, { id: rawId });
                if (data.status === "success") setExpenseDetail(data.payment);
                else alert(data.message || "Не удалось загрузить операцию");
            } catch (err) {
                console.error("getExpensePaymentDetail:", err);
                alert("Ошибка соединения с сервером");
            } finally {
                setDetailLoadingId(null);
            }
            return;
        }
        if (op.orderNumber == null || !NAVIGABLE_TYPES.includes(op.requestType)) return;
        const key = `${op.requestType}-${op.orderNumber}`;
        setDetailLoadingId(key);
        try {
            const { data } = await axios.post(`${API_URL}/getOrderDetail`, {
                orderNumber: op.orderNumber,
                requestType: op.requestType,
            });
            if (data.status === "success") setDetailOrder(data.order);
        } catch (err) {
            console.error("getOrderDetail:", err);
        } finally {
            setDetailLoadingId(null);
        }
    };

    const formatAmount = (op) => {
        const sign = op.kind === "income" ? "+" : "−";
        const val = Math.abs(op.amount).toLocaleString("ru-RU", { maximumFractionDigits: 2 });
        const usd = op.amountUsd != null && op.currency !== "USD"
            ? ` (≈ $${Math.abs(op.amountUsd).toLocaleString("ru-RU", { maximumFractionDigits: 2 })})`
            : "";
        return `${sign}${val} ${op.currency || ""}${usd}`;
    };

    const formatDate = (d) => {
        if (!d) return "—";
        const [y, m, day] = String(d).slice(0, 10).split("-");
        return `${day}.${m}.${y}`;
    };

    const formatTime = (createdAt) => {
        if (!createdAt) return null;
        return new Date(createdAt).toLocaleTimeString("ru-RU", { hour: "2-digit", minute: "2-digit" });
    };

    const currencies = Object.keys(totals);
    const s = search.trim().toLowerCase();
    const filteredOperations = s
        ? operations.filter((op) => {
            const hay = [op.label, op.personName, op.comment, op.payment, op.orderNumber]
                .filter(Boolean)
                .join(" ")
                .toLowerCase();
            return hay.includes(s);
        })
        : operations;
    return (
        <div className="op-page">
            <div className="op-header">
                <h1 className="op-title">Операции</h1>

                <div className="op-mode-switch">
                    <button
                        className={`op-mode-btn ${filterMode === "month" ? "op-mode-btn--active" : ""}`}
                        onClick={() => setFilterMode("month")}
                    >
                        По месяцу
                    </button>
                    <button
                        className={`op-mode-btn ${filterMode === "range" ? "op-mode-btn--active" : ""}`}
                        onClick={() => setFilterMode("range")}
                    >
                        Диапазон дат
                    </button>
                </div>
            </div>

            <div className="op-filters">
                {filterMode === "month" ? (
                    <div className="op-month-nav">
                        <button className="op-nav-btn" onClick={goPrevMonth} aria-label="Предыдущий месяц">
                            <i className="fa-solid fa-chevron-left" />
                        </button>
                        <span className="op-month-label">{MONTHS_RU[month - 1]} {year}</span>
                        <button className="op-nav-btn" onClick={goNextMonth} aria-label="Следующий месяц">
                            <i className="fa-solid fa-chevron-right" />
                        </button>
                    </div>
                ) : (
                    <div className="op-range-inputs">
                        <input
                            type="date"
                            className="op-date-input"
                            value={dateFrom}
                            max={dateTo}
                            onChange={(e) => setDateFrom(e.target.value)}
                        />
                        <span className="op-range-sep">—</span>
                        <input
                            type="date"
                            className="op-date-input"
                            value={dateTo}
                            min={dateFrom}
                            onChange={(e) => setDateTo(e.target.value)}
                        />
                    </div>
                )}
                <input
                    type="text"
                    className="op-search-input"
                    placeholder="Поиск: имя, название, комментарий, касса, № заказа..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                />
                <div className="op-kind-filter">
                    <button
                        className={`op-kind-btn ${kind === "all" ? "op-kind-btn--active" : ""}`}
                        onClick={() => setKind("all")}
                    >
                        Все
                    </button>
                    <button
                        className={`op-kind-btn op-kind-btn--income ${kind === "income" ? "op-kind-btn--active" : ""}`}
                        onClick={() => setKind("income")}
                    >
                        Приход
                    </button>
                    <button
                        className={`op-kind-btn op-kind-btn--expense ${kind === "expense" ? "op-kind-btn--active" : ""}`}
                        onClick={() => setKind("expense")}
                    >
                        Расход
                    </button>
                </div>
            </div>

            {currencies.length > 0 && (
                <div className="op-totals">
                    {currencies.map((cur) => (
                        <div className="op-total-card" key={cur}>
                            <span className="op-total-currency">{cur}</span>
                            <span className="op-total-income">
                                +{totals[cur].income.toLocaleString("ru-RU", { maximumFractionDigits: 2 })}
                            </span>
                            <span className="op-total-expense">
                                −{totals[cur].expense.toLocaleString("ru-RU", { maximumFractionDigits: 2 })}
                            </span>
                        </div>
                    ))}
                </div>
            )}

            <div className="op-list">
                {loading ? (
                    <div className="op-empty">Загрузка...</div>
                ) : filteredOperations.length === 0 ? (
                    <div className="op-empty">Операций за этот период нет</div>
                ) : (
                    filteredOperations.map((op) => {
                        const isExpense = op.type === "expense_payment";
                        const clickable = isExpense || (op.orderNumber != null && NAVIGABLE_TYPES.includes(op.requestType));
                        const key = isExpense ? op.id : `${op.requestType}-${op.orderNumber}`;
                        const isLoadingThis = detailLoadingId === key;
                        const rawId = op.id.split("-")[1];
                        return (
                            <div
                                key={op.id}
                                className={`op-row op-row--${op.kind} ${clickable ? "op-row--clickable" : ""} ${op.isAnnulled ? "op-row--annulled" : ""}`}
                                onClick={() => handleRowClick(op)}
                            >
                                <div className="op-row-date">
                                    {formatDate(op.date)}
                                    {op.createdAt && <span className="op-row-time">{formatTime(op.createdAt)}</span>}
                                </div>
                                <div className="op-row-main">
                                    <span className="op-row-label">
                                        {op.isAnnulled && <span className="op-row-annul-tag">Аннулировано</span>}
                                        {op.orderNumber == null && (op.type === "expense_payment" || op.type === "kassa_income") && (
                                            <span className={`op-row-misc-tag op-row-misc-tag--${op.kind}`}>
                                                {op.kind === "income" ? "Прочий приход" : "Прочий расход"}
                                            </span>
                                        )}
                                        {op.label}
                                        {isLoadingThis && <span className="op-row-loading">…</span>}
                                    </span>
                                    {op.personName && (
                                        <span className="op-row-sub">{op.role === "guide" ? "Гид" : op.role === "driver" ? "Водитель" : op.role === "cafe" ? "Кафе" : ""} · {op.personName}</span>
                                    )}
                                    {op.comment && <span className="op-row-comment">{op.comment}</span>}
                                    {op.isAnnulled && op.annulComment && (
                                        <span className="op-row-comment op-row-comment--annul">Причина: {op.annulComment}</span>
                                    )}
                                    <span className="op-row-payment">{op.payment}</span>
                                    {op.type === "expense_payment" && op.hasSignature && (
                                        <span className="op-row-signed"><i className="fa-solid fa-signature" /> Подписано</span>
                                    )}
                                </div>
                                <div className={`op-row-amount op-row-amount--${op.kind}`}>
                                    {formatAmount(op)}
                                </div>
                                {!op.isAnnulled && (
                                    <div className="op-row-actions">
                                        {op.type === "order_payment" && (
                                            <button
                                                className="op-row-print-btn"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleReprint(op, rawId);
                                                }}
                                                disabled={reprintLoadingId === op.id}
                                                title="Повторно напечатать чек"
                                            >
                                                <i className={reprintLoadingId === op.id ? "fa-solid fa-spinner fa-spin" : "fa-solid fa-print"} />
                                            </button>
                                        )}
                                        <button
                                            className="op-row-annul-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                setAnnulTarget({ type: op.type, rawId, label: op.label });
                                            }}
                                            title="Аннулировать операцию"
                                        >
                                            <i className="fa-solid fa-rotate-left" />
                                        </button>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>

            {annulTarget && (
                <div className="op-annul-overlay" onClick={() => !annulling && setAnnulTarget(null)}>
                    <div className="op-annul-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="op-annul-title">Аннулировать операцию?</div>
                        <div className="op-annul-desc">{annulTarget.label}</div>
                        <textarea
                            className="op-annul-comment"
                            rows={2}
                            placeholder="Причина (необязательно)"
                            value={annulComment}
                            onChange={(e) => setAnnulComment(e.target.value)}
                        />
                        <div className="op-annul-actions">
                            <button className="op-annul-cancel" onClick={() => setAnnulTarget(null)} disabled={annulling}>
                                Отмена
                            </button>
                            <button className="op-annul-confirm" onClick={confirmAnnul} disabled={annulling}>
                                {annulling ? "Аннулируем…" : "Аннулировать"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
            <OrderDetailModal order={detailOrder} onClose={() => setDetailOrder(null)} />

            {expenseDetail && (
                <div className="op-annul-overlay" onClick={() => setExpenseDetail(null)}>
                    <div className="op-annul-modal op-sign-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="op-annul-title">
                            {expenseDetail.isAnnulled && <span className="op-row-annul-tag">Аннулировано</span>}
                            {expenseDetail.title}
                        </div>
                        <div className="op-sign-lines">
                            {expenseDetail.personName && (
                                <div><span>Получатель</span>{ROLE_LABELS[expenseDetail.role] ? `${ROLE_LABELS[expenseDetail.role]} · ` : ""}{expenseDetail.personName}</div>
                            )}
                            {expenseDetail.orderNumber != null && <div><span>Заказ</span>№{expenseDetail.orderNumber}</div>}
                            <div><span>Сумма</span>{formatAmount({ ...expenseDetail, kind: "expense", amountUsd: null })}</div>
                            <div><span>Касса</span>{expenseDetail.payment}</div>
                            <div><span>Дата</span>{formatDate(expenseDetail.date)} {formatTime(expenseDetail.createdAt) || ""}</div>
                            {expenseDetail.comment && <div><span>Комментарий</span>{expenseDetail.comment}</div>}
                            {expenseDetail.isAnnulled && expenseDetail.annulComment && (
                                <div><span>Причина аннулирования</span>{expenseDetail.annulComment}</div>
                            )}
                        </div>
                        <div className="op-sign-label">Подпись получателя</div>
                        <SignatureView signature={expenseDetail.signature} />
                        <div className="op-annul-actions">
                            <button className="op-annul-cancel" onClick={() => setExpenseDetail(null)}>Закрыть</button>
                        </div>
                    </div>
                </div>
            )}

            {pendingReceipt ? <ReceiptLangModal onPick={handleLangPick} /> : null}
            <ReceiptPrintout data={receipt} />
        </div>
    );
};

export default Operations;